import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../auth/AuthAtoms';
import { handleLogin } from '../auth/AuthHelper';
import { getGoogleSignInUrl, socialLoginWithApple } from '../api/auth';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [appleLoading, setAppleLoading] = useState(false);
    const [currentUser, setCurrentUser] = useAtom(currentUserAtom);

    const navigate = useNavigate();

    const handleLoginFunction = async e => {
        e.preventDefault();
        setLoading(true);
        try {
            await handleLogin(username, password);
            toast.success('Login successful');
            navigate('/'); // Redirect to home page on successful login
        } catch (error) {
            toast.error('Login failed: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setGoogleLoading(true);
        try {
            const url = await getGoogleSignInUrl('login');
            window.location.href = url;
        } catch (err) {
            toast.error('Failed to start Google sign-in');
            setGoogleLoading(false);
        }
    };

    const handleAppleSignIn = async () => {
        setAppleLoading(true);
        try {
            const result = await socialLoginWithApple();

            if (result.status === 'account_exists') {
                toast.error(result.message || 'An account with this email already exists. Log in with your password and link Apple from Account settings.');
                return;
            }

            if (result.status === 'username_required') {
                sessionStorage.setItem('apple_identity_token', result._identityToken);
                sessionStorage.setItem('apple_nonce', result._nonce);
                sessionStorage.setItem('social_provider', 'apple');
                navigate('/auth/choose-username');
                return;
            }

            if (!result.AccessToken) {
                toast.error('Apple sign-in failed: no token received');
                return;
            }

            localStorage.setItem('accessToken', result.AccessToken);
            localStorage.setItem('authOrigin', 'apple');
            if (result.RefreshToken) localStorage.setItem('refreshToken', result.RefreshToken);
            if (result.user) {
                setCurrentUser(result.user);
                localStorage.setItem('username', result.user.username);
            }
            toast.success('Login successful');
            navigate('/');
        } catch (err) {
            if (err.message !== 'Sign-in cancelled') {
                toast.error('Apple sign-in failed: ' + err.message);
            }
        } finally {
            setAppleLoading(false);
        }
    };

    return (
        <div>
            <h2>Login</h2>
            {currentUser ? (
                <div>Looks like you are already logged in, {currentUser.username}!</div>
            ) : (
                <>
                    <button
                        className="google-signin-btn"
                        onClick={handleGoogleSignIn}
                        disabled={googleLoading}
                        type="button"
                    >
                        <img src="/google-logo.svg" alt="Google" className="google-signin-icon" />
                        {googleLoading ? 'Redirecting...' : 'Sign in with Google'}
                    </button>
                    <button
                        className="apple-signin-btn"
                        onClick={handleAppleSignIn}
                        disabled={appleLoading}
                        type="button"
                    >
                        <span className="apple-signin-icon"></span>
                        {appleLoading ? 'Signing in...' : 'Sign in with Apple'}
                    </button>
                    <div className="login-divider">
                        <span>or</span>
                    </div>
                    <form onSubmit={handleLoginFunction}>
                        <label>
                            Username:
                            <input type="text" value={username} onChange={e => setUsername(e.target.value)} required />
                        </label>
                        <label>
                            Password:
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                        </label>
                        <button type="submit" disabled={loading}>
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                        <p>
                            <Link to="/forgot-password">Forgot password?</Link>
                        </p>
                    </form>
                    <p className="tc-consent">
                        By logging in, you agree to our <Link to="/terms">Terms</Link>.
                    </p>
                </>
            )}
        </div>
    );
};

export default Login;
